import { useMemo, useState } from 'react';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { IconPlus, IconTrash } from '@tabler/icons-react';

import { DEVICE_TYPES, useGetDevicesBySite } from 'api/devices';
import useTenantSelection from 'hooks/useTenantSelection';
import { PORT_CONFIG_OPTIONS } from '../../../utils/portConfigs';
import PortConfigComponent, { TcpPortConfig } from './PortConfig';

// Mirrors Weightrax.Domain.Enums.DirectionEnum
const IGNORE_REPEAT_REGO_MAX_SECONDS = 300;

const DIRECTION_OPTIONS: { value: number; label: string }[] = [
  { value: 1, label: 'In' },
  { value: 2, label: 'Out' },
  { value: 3, label: 'Both' }
];

// Mirrors Weightrax.Domain.Enums.DeviceType (AppMode context)
const APP_MODE_OPTIONS: { value: number | null; label: string }[] = [
  { value: null, label: 'All Modes' },
  ...DEVICE_TYPES.map((t) => ({ value: t.id as number | null, label: t.name }))
];

export type AnprDestination = {
  location: string;
  deviceId: string;
  appMode: number | null;
  direction: number;
};

export type AnprConfig = {
  name: string;
  cameraConfig: TcpPortConfig;
  serviceClassName: string;
  selfTest: boolean;
  ignoreRepeatRegoForSeconds: number;
  destinations: AnprDestination[];
};

type AnprPeripheralConfigProps = {
  value: AnprConfig;
  onChange: (next: AnprConfig) => void;
  disabled?: boolean;
};

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {};
}

function asString(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

function asNumber(value: unknown, fallback = 0): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function asBoolean(value: unknown, fallback = false): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

function clampIgnoreRepeatRegoSeconds(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.min(IGNORE_REPEAT_REGO_MAX_SECONDS, Math.max(0, Math.round(value)));
}

function parseDirection(value: unknown): number {
  if (typeof value === 'number') return [1, 2, 3].includes(value) ? value : 1;
  if (value === 'In' || value === 'in') return 1;
  if (value === 'Out' || value === 'out') return 2;
  if (value === 'Both' || value === 'both') return 3;
  return 1;
}

function parseAppMode(value: unknown): number | null {
  if (value === null || value === undefined) return null;
  const n = Number(value);
  return [1, 2, 3].includes(n) ? n : null;
}

function buildDefaultTcpConfig(base?: unknown): TcpPortConfig {
  const source = asRecord(base);
  return {
    type: PORT_CONFIG_OPTIONS.TcpIp,
    host: asString(source.host ?? source.Host, '127.0.0.1'),
    port: asNumber(source.port ?? source.Port, 5000)
  };
}

function buildDestination(source: unknown): AnprDestination {
  const rec = asRecord(source);
  return {
    location: asString(rec.location ?? rec.Location, ''),
    deviceId: asString(rec.deviceId ?? rec.DeviceId, ''),
    appMode: parseAppMode(rec.appMode ?? rec.AppMode),
    direction: parseDirection(rec.direction ?? rec.Direction)
  };
}

export function buildDefaultAnprConfig(base?: unknown): AnprConfig {
  const source = asRecord(base);

  let destinations: AnprDestination[];
  const rawDestinations = Array.isArray(source.destinations)
    ? source.destinations
    : Array.isArray(source.Destinations)
      ? (source.Destinations as unknown[])
      : null;
  if (rawDestinations != null) {
    destinations = rawDestinations.map(buildDestination);
  } else {
    // Migrate from old single-direction format
    const oldDirection = source.direction ?? source.Direction;
    const oldLocation = asString(source.location ?? source.Location, '');
    destinations =
      oldDirection != null ? [{ location: oldLocation, deviceId: '', appMode: null, direction: parseDirection(oldDirection) }] : [];
  }

  return {
    name: asString(source.name ?? source.Name, ''),
    cameraConfig: buildDefaultTcpConfig(source.cameraConfig ?? source.CameraConfig),
    serviceClassName: asString(source.serviceClassName ?? source.ServiceClassName, ''),
    selfTest: asBoolean(source.selfTest ?? source.SelfTest, false),
    ignoreRepeatRegoForSeconds: clampIgnoreRepeatRegoSeconds(
      asNumber(source.ignoreRepeatRegoForSeconds ?? source.IgnoreRepeatRegoForSeconds, 0)
    ),
    destinations
  };
}

function toJson(value: AnprConfig): unknown {
  return {
    name: value.name,
    cameraConfig: { host: value.cameraConfig.host, port: value.cameraConfig.port },
    serviceClassName: value.serviceClassName,
    selfTest: value.selfTest,
    ignoreRepeatRegoForSeconds: value.ignoreRepeatRegoForSeconds,
    destinations: value.destinations.map((d) => ({
      location: d.location,
      deviceId: d.deviceId,
      appMode: d.appMode,
      direction: d.direction
    }))
  };
}

const TCP_ONLY = [PORT_CONFIG_OPTIONS.TcpIp] as (typeof PORT_CONFIG_OPTIONS.TcpIp)[];

export default function ANPRPeripheralConfig({ value, onChange, disabled = false }: AnprPeripheralConfigProps) {
  const { selectedCompanySiteResult } = useTenantSelection();
  const siteId = selectedCompanySiteResult?.siteId ?? null;
  const { devices } = useGetDevicesBySite(siteId);

  const [advancedMode, setAdvancedMode] = useState(false);
  const [jsonError, setJsonError] = useState<string | null>(null);

  const advancedJson = useMemo(() => JSON.stringify(toJson(value), null, 2), [value]);
  const [jsonText, setJsonText] = useState(advancedJson);

  const handleModeToggle = (checked: boolean) => {
    setAdvancedMode(checked);
    setJsonError(null);
    if (checked) setJsonText(advancedJson);
  };

  const handleApplyJson = () => {
    try {
      const parsed = JSON.parse(jsonText);
      onChange(buildDefaultAnprConfig(parsed));
      setJsonError(null);
    } catch {
      setJsonError('Configuration JSON is invalid. Fix the JSON and try again.');
    }
  };

  const updateDestination = (index: number, patch: Partial<AnprDestination>) => {
    onChange({
      ...value,
      destinations: value.destinations.map((d, i) => (i === index ? { ...d, ...patch } : d))
    });
  };

  const addDestination = () => {
    const newDest: AnprDestination = { location: '', deviceId: '', appMode: null, direction: 1 };
    onChange({ ...value, destinations: [...value.destinations, newDest] });
  };

  const removeDestination = (index: number) => {
    onChange({ ...value, destinations: value.destinations.filter((_, i) => i !== index) });
  };

  return (
    <Stack spacing={2}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          ANPR Configuration
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Simple
          </Typography>
          <Switch checked={advancedMode} onChange={(_, checked) => handleModeToggle(checked)} disabled={disabled} />
          <Typography variant="body2" color="text.secondary">
            Advanced
          </Typography>
        </Box>
      </Box>

      {!advancedMode ? (
        <Stack spacing={2.5}>
          {/* Camera Connection */}
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Camera Connection
          </Typography>
          <PortConfigComponent
            value={value.cameraConfig}
            onChange={(next) => onChange({ ...value, cameraConfig: next as TcpPortConfig })}
            allowedTypes={TCP_ONLY}
            disabled={disabled}
          />

          <Divider />

          {/* Camera Settings */}
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Camera Settings
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Service Class Name"
                placeholder="e.g. TattileCameraService"
                value={value.serviceClassName}
                onChange={(e) => onChange({ ...value, serviceClassName: e.target.value })}
                disabled={disabled}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={value.selfTest}
                    onChange={(_, checked) => onChange({ ...value, selfTest: checked })}
                    disabled={disabled}
                  />
                }
                label="Self Test"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                type="number"
                label="Ignore Repeat Rego (seconds)"
                inputProps={{ min: 0, max: IGNORE_REPEAT_REGO_MAX_SECONDS, step: 1 }}
                value={value.ignoreRepeatRegoForSeconds}
                helperText={`Ignore the same rego seen again within this many seconds. 0 = off, max ${IGNORE_REPEAT_REGO_MAX_SECONDS}`}
                onChange={(e) => onChange({ ...value, ignoreRepeatRegoForSeconds: clampIgnoreRepeatRegoSeconds(Number(e.target.value)) })}
                disabled={disabled}
              />
            </Grid>
          </Grid>

          <Divider />

          {/* Destinations */}
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Destinations
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Devices that receive ANPR events from this camera, filtered by app mode and lane direction.
              </Typography>
            </Box>
            <Button
              size="small"
              variant="outlined"
              startIcon={<IconPlus size={15} stroke={2} />}
              onClick={addDestination}
              disabled={disabled}
            >
              Add
            </Button>
          </Stack>

          {value.destinations.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No destinations configured. Add one above.
            </Typography>
          ) : (
            <Stack spacing={1.5}>
              {value.destinations.map((dest, index) => (
                <Grid container spacing={1.5} key={index} alignItems="center">
                  <Grid size={{ xs: 12, md: 3.5 }}>
                    <TextField
                      fullWidth
                      select
                      size="small"
                      label="Device"
                      value={dest.deviceId}
                      onChange={(e) => {
                        const device = devices.find((d) => d.deviceId === e.target.value);
                        updateDestination(index, {
                          deviceId: e.target.value,
                          location: dest.location || device?.location || ''
                        });
                      }}
                      disabled={disabled}
                    >
                      <MenuItem value="">
                        <em>{devices.length === 0 ? 'No devices on this site' : '— Select device —'}</em>
                      </MenuItem>
                      {devices.map((d) => (
                        <MenuItem key={d.deviceId} value={d.deviceId}>
                          {d.deviceName}
                          {d.location ? ` (${d.location})` : ''}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid size={{ xs: 6, md: 2 }}>
                    <TextField
                      fullWidth
                      select
                      size="small"
                      label="App Mode"
                      value={dest.appMode ?? ''}
                      onChange={(e) => updateDestination(index, { appMode: e.target.value === '' ? null : Number(e.target.value) })}
                      disabled={disabled}
                    >
                      {APP_MODE_OPTIONS.map((opt) => (
                        <MenuItem key={String(opt.value)} value={opt.value ?? ''}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid size={{ xs: 6, md: 2 }}>
                    <TextField
                      fullWidth
                      select
                      size="small"
                      label="Direction"
                      value={dest.direction}
                      onChange={(e) => updateDestination(index, { direction: Number(e.target.value) })}
                      disabled={disabled}
                    >
                      {DIRECTION_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid size={{ xs: 12, md: 'auto' }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Location"
                      placeholder="e.g. Entrance"
                      value={dest.location}
                      onChange={(e) => updateDestination(index, { location: e.target.value })}
                      disabled={disabled}
                    />
                  </Grid>
                  <Grid size={{ xs: 'auto' }}>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => removeDestination(index)}
                      disabled={disabled}
                      title="Remove destination"
                    >
                      <IconTrash size={16} stroke={1.5} />
                    </IconButton>
                  </Grid>
                </Grid>
              ))}
            </Stack>
          )}
        </Stack>
      ) : (
        <Stack spacing={1.5}>
          <TextField
            fullWidth
            multiline
            minRows={14}
            label="Configuration JSON"
            value={jsonText}
            onChange={(e) => setJsonText(e.target.value)}
            disabled={disabled}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="outlined" onClick={handleApplyJson} disabled={disabled}>
              Apply JSON
            </Button>
          </Box>
          {jsonError ? <Alert severity="error">{jsonError}</Alert> : null}
        </Stack>
      )}
    </Stack>
  );
}
